<script setup>
	import { reactive } from "vue";

	const pageBlocks = [
		{
			type: "hero",
			eyebrow: "Business Solutions Evaluations",
			title: "Placeholder headline for the evaluations offering.",
			lead: "Placeholder intro copy — replace with the overview Nancy provides. A sentence or two on what a Business Solutions Evaluation is and who it's for.",
			primaryCta: { label: "Get Started", route: "contact" },
			secondaryCta: { label: "Explore services", route: "services" },
			highlights: [
				"Placeholder highlight one",
				"Placeholder highlight two",
				"Placeholder highlight three",
			],
		},
		{
			type: "stats",
			items: [
				{ value: "00+", label: "Placeholder stat label" },
				{ value: "00", label: "Placeholder stat label" },
				{ value: "00%", label: "Placeholder stat label" },
				{ value: "00", label: "Placeholder stat label" },
			],
		},
		{
			type: "carousel",
			eyebrow: "Placeholder section label",
			heading: "Carousel placeholder heading",
			sub: "Replace these slides with the real content. Each slide takes a short tag, a title, and a paragraph; add or remove slides freely.",
			slides: [
				{ tag: "Slide 01", title: "Placeholder slide title", text: "Placeholder slide body copy. This is where a single evaluation theme, finding, or step would be described in a sentence or two." },
				{ tag: "Slide 02", title: "Placeholder slide title", text: "Placeholder slide body copy. Swap in the real content once it's ready — the carousel adapts to however many slides are in the array." },
				{ tag: "Slide 03", title: "Placeholder slide title", text: "Placeholder slide body copy. Keep each one roughly the same length for a tidy layout." },
				{ tag: "Slide 04", title: "Placeholder slide title", text: "Placeholder slide body copy. Arrows, dots, and keyboard arrows all move between slides." },
			],
		},
		{
			type: "cards",
			eyebrow: "Placeholder section label",
			heading: "What we evaluate (placeholder)",
			sub: "A flexible grid that reflows to the number of items. Use it for evaluation areas, criteria, or deliverables.",
			items: [
				{ icon: "search", title: "Placeholder card title", text: "Placeholder card description — one or two sentences describing this point." },
				{ icon: "shield", title: "Placeholder card title", text: "Placeholder card description — one or two sentences describing this point." },
				{ icon: "chart", title: "Placeholder card title", text: "Placeholder card description — one or two sentences describing this point." },
				{ icon: "layers", title: "Placeholder card title", text: "Placeholder card description — one or two sentences describing this point." },
				{ icon: "chat", title: "Placeholder card title", text: "Placeholder card description — one or two sentences describing this point." },
				{ icon: "check", title: "Placeholder card title", text: "Placeholder card description — one or two sentences describing this point." },
			],
		},
		{
			type: "split",
			eyebrow: "Placeholder section label",
			heading: "Split section placeholder heading",
			text: "Placeholder body copy for an alternating visual-and-text section. Drop a screenshot, diagram, or illustration into the visual area once it's available.",
			bullets: [
				"Placeholder supporting point",
				"Placeholder supporting point",
				"Placeholder supporting point",
			],
			cta: { label: "Get Started", route: "contact" },
			flip: false,
		},
		{
			type: "split",
			eyebrow: "Placeholder section label",
			heading: "Second split, mirrored",
			text: "Placeholder body copy. This is the same block with flip set to true, so content can alternate sides as you scroll down the page.",
			bullets: [
				"Placeholder supporting point",
				"Placeholder supporting point",
			],
			cta: null,
			flip: true,
		},
		{
			type: "quote",
			text: "Placeholder pull quote — drop in a client testimonial or a one-line summary of the value this offering delivers.",
			author: "Placeholder Name",
			role: "Placeholder Title, Company",
		},
		{
			type: "cta",
			title: "Placeholder closing call to action",
			lead: "Replace with a final prompt that points visitors toward the next step you want them to take.",
			cta: { label: "Get Started", route: "contact" },
			note: "Placeholder supporting note (optional).",
		},
	];

	const carouselIndexByBlock = reactive({});

	function carouselCurrent(blockIndex) {
		return carouselIndexByBlock[blockIndex] || 0;
	}

	function carouselSet(blockIndex, slideIndex) {
		carouselIndexByBlock[blockIndex] = slideIndex;
	}

	function carouselStep(blockIndex, direction, slideCount) {
		const current = carouselIndexByBlock[blockIndex] || 0;
		carouselIndexByBlock[blockIndex] = (current + direction + slideCount) % slideCount;
	}

	function onCarouselKey(event, blockIndex, slideCount) {
		if (event.key === "ArrowLeft") {
			event.preventDefault();
			carouselStep(blockIndex, -1, slideCount);
		} else if (event.key === "ArrowRight") {
			event.preventDefault();
			carouselStep(blockIndex, 1, slideCount);
		}
	}
</script>

<template>
	<div class="bse">
		<template v-for="(block, blockIndex) in pageBlocks" :key="blockIndex">
			<section v-if="block.type === 'hero'" class="block block-hero">
				<div class="hero-bg" aria-hidden="true">
					<span class="hero-blob hero-blob-one"></span>
					<span class="hero-blob hero-blob-two"></span>
					<span class="hero-grid"></span>
				</div>
				<div class="block-inner hero-inner">
					<span v-if="block.eyebrow" class="eyebrow rise">{{ block.eyebrow }}</span>
					<h1 class="hero-title rise rise-1">{{ block.title }}</h1>
					<p v-if="block.lead" class="hero-lead rise rise-2">{{ block.lead }}</p>
					<div v-if="block.primaryCta || block.secondaryCta" class="hero-actions rise rise-3">
						<RouterLink v-if="block.primaryCta" :to="{ name: block.primaryCta.route }" class="btn btn-primary">{{ block.primaryCta.label }}</RouterLink>
						<RouterLink v-if="block.secondaryCta" :to="{ name: block.secondaryCta.route }" class="btn btn-ghost">{{ block.secondaryCta.label }}</RouterLink>
					</div>
					<ul v-if="block.highlights && block.highlights.length" class="hero-highlights rise rise-4">
						<li v-for="highlight in block.highlights" :key="highlight"><span class="highlight-dot" aria-hidden="true"></span> {{ highlight }}</li>
					</ul>
				</div>
			</section>

			<section v-else-if="block.type === 'stats'" class="block block-stats">
				<div class="block-inner">
					<header v-if="block.heading || block.eyebrow" class="block-header">
						<span v-if="block.eyebrow" class="block-eyebrow">{{ block.eyebrow }}</span>
						<h2 v-if="block.heading" class="block-heading">{{ block.heading }}</h2>
						<p v-if="block.sub" class="block-sub">{{ block.sub }}</p>
					</header>
					<div class="stats-grid">
						<div v-for="(stat, statIndex) in block.items" :key="statIndex" class="stat">
							<span class="stat-value">{{ stat.value }}</span>
							<span class="stat-label">{{ stat.label }}</span>
						</div>
					</div>
				</div>
			</section>

			<section v-else-if="block.type === 'carousel'" class="block block-carousel">
				<div class="block-inner">
					<header v-if="block.heading || block.eyebrow" class="block-header">
						<span v-if="block.eyebrow" class="block-eyebrow">{{ block.eyebrow }}</span>
						<h2 v-if="block.heading" class="block-heading">{{ block.heading }}</h2>
						<p v-if="block.sub" class="block-sub">{{ block.sub }}</p>
					</header>

					<div class="carousel" role="group" aria-roledescription="carousel" aria-label="Content carousel" tabindex="0" @keydown="onCarouselKey($event, blockIndex, block.slides.length)">
						<div class="carousel-viewport">
							<div class="carousel-track" :style="{ transform: 'translateX(' + (carouselCurrent(blockIndex) * -100) + '%)' }">
								<div
									v-for="(slide, slideIndex) in block.slides"
									:key="slideIndex"
									class="carousel-slide"
									role="group"
									aria-roledescription="slide"
									:aria-hidden="carouselCurrent(blockIndex) !== slideIndex ? 'true' : 'false'"
								>
									<div class="carousel-card">
										<span v-if="slide.tag" class="carousel-tag">{{ slide.tag }}</span>
										<h3 v-if="slide.title" class="carousel-card-title">{{ slide.title }}</h3>
										<p v-if="slide.text" class="carousel-card-text">{{ slide.text }}</p>
									</div>
								</div>
							</div>
						</div>

						<div class="carousel-controls">
							<button type="button" class="carousel-arrow" aria-label="Previous slide" @click="carouselStep(blockIndex, -1, block.slides.length)">
								<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M15 6l-6 6 6 6" /></svg>
							</button>
							<div class="carousel-dots">
								<button
									v-for="(slide, dotIndex) in block.slides"
									:key="dotIndex"
									type="button"
									class="carousel-dot"
									:class="{ 'carousel-dot-active': carouselCurrent(blockIndex) === dotIndex }"
									:aria-label="`Go to slide ${dotIndex + 1}`"
									:aria-current="carouselCurrent(blockIndex) === dotIndex ? 'true' : 'false'"
									@click="carouselSet(blockIndex, dotIndex)"
								></button>
							</div>
							<button type="button" class="carousel-arrow" aria-label="Next slide" @click="carouselStep(blockIndex, 1, block.slides.length)">
								<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M9 6l6 6-6 6" /></svg>
							</button>
						</div>
					</div>
				</div>
			</section>

			<section v-else-if="block.type === 'cards'" class="block block-cards">
				<div class="block-inner">
					<header v-if="block.heading || block.eyebrow" class="block-header">
						<span v-if="block.eyebrow" class="block-eyebrow">{{ block.eyebrow }}</span>
						<h2 v-if="block.heading" class="block-heading">{{ block.heading }}</h2>
						<p v-if="block.sub" class="block-sub">{{ block.sub }}</p>
					</header>
					<div class="cards-grid">
						<article v-for="(card, cardIndex) in block.items" :key="cardIndex" class="content-card">
							<span class="content-card-icon" aria-hidden="true">
								<svg v-if="card.icon === 'search'" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="11" cy="11" r="7" /><path d="M21 21l-4.3-4.3" /></svg>
								<svg v-else-if="card.icon === 'shield'" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M12 3l7 3v6c0 4.5-3 7.6-7 9-4-1.4-7-4.5-7-9V6l7-3z" /></svg>
								<svg v-else-if="card.icon === 'chart'" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M3 21h18" /><path d="M6 21v-7M12 21V5M18 21v-10" /></svg>
								<svg v-else-if="card.icon === 'layers'" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M12 3l9 5-9 5-9-5 9-5z" /><path d="M3 12l9 5 9-5" /></svg>
								<svg v-else-if="card.icon === 'chat'" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 11.5a8.38 8.38 0 01-8.5 8.5 8.5 8.5 0 01-3.8-.9L3 21l1.9-5.7a8.5 8.5 0 01-.9-3.8 8.38 8.38 0 018.5-8.5 8.5 8.5 0 018.5 8.5z" /></svg>
								<svg v-else-if="card.icon === 'check'" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M9 12l2 2 4-4" /><circle cx="12" cy="12" r="9" /></svg>
								<svg v-else viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="9" /><path d="M12 8v4l3 2" /></svg>
							</span>
							<h3 v-if="card.title" class="content-card-title">{{ card.title }}</h3>
							<p v-if="card.text" class="content-card-text">{{ card.text }}</p>
						</article>
					</div>
				</div>
			</section>

			<section v-else-if="block.type === 'split'" class="block block-split">
				<div class="block-inner">
					<div class="split-grid" :class="{ 'split-flip': block.flip }">
						<div class="split-visual" aria-hidden="true">
							<span class="split-visual-label">Visual placeholder</span>
						</div>
						<div class="split-content">
							<span v-if="block.eyebrow" class="block-eyebrow">{{ block.eyebrow }}</span>
							<h2 v-if="block.heading" class="split-heading">{{ block.heading }}</h2>
							<p v-if="block.text" class="split-text">{{ block.text }}</p>
							<ul v-if="block.bullets && block.bullets.length" class="split-bullets">
								<li v-for="bullet in block.bullets" :key="bullet">
									<span class="split-bullet-icon" aria-hidden="true">
										<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"><path d="M5 13l4 4L19 7" /></svg>
									</span>
									{{ bullet }}
								</li>
							</ul>
							<RouterLink v-if="block.cta" :to="{ name: block.cta.route }" class="btn btn-primary split-cta">{{ block.cta.label }}</RouterLink>
						</div>
					</div>
				</div>
			</section>

			<section v-else-if="block.type === 'quote'" class="block block-quote">
				<div class="block-inner quote-inner">
					<span class="quote-mark" aria-hidden="true">&ldquo;</span>
					<p class="quote-text">{{ block.text }}</p>
					<p v-if="block.author || block.role" class="quote-author">
						<span v-if="block.author" class="quote-author-name">{{ block.author }}</span>
						<span v-if="block.role" class="quote-author-role">{{ block.role }}</span>
					</p>
				</div>
			</section>

			<section v-else-if="block.type === 'cta'" class="block block-cta">
				<div class="block-inner">
					<div class="cta-card">
						<h2 v-if="block.title" class="cta-title">{{ block.title }}</h2>
						<p v-if="block.lead" class="cta-lead">{{ block.lead }}</p>
						<div v-if="block.cta" class="cta-actions">
							<RouterLink :to="{ name: block.cta.route }" class="btn btn-on-blue">{{ block.cta.label }}</RouterLink>
						</div>
						<p v-if="block.note" class="cta-note">{{ block.note }}</p>
					</div>
				</div>
			</section>
		</template>
	</div>
</template>

<style scoped>
	.bse {
		--bg: #f5f9fd;
		--surface: #ffffff;
		--surface-muted: #eef4fb;
		--border: #e2ecf6;
		--border-strong: #cddcec;
		--navy: #0f2742;
		--ink: #16314f;
		--ink-soft: #4a5d75;
		--ink-muted: #7589a1;
		--sky: #0ea5e9;
		--sky-deep: #0284c7;
		--blue: #2563eb;
		--blue-deep: #1d4ed8;
		--blue-soft: #dceafe;

		font-family: "Poppins", "Segoe UI", -apple-system, BlinkMacSystemFont, "Helvetica Neue", Arial, sans-serif;
		color: var(--ink);
		background: var(--bg);
		overflow-x: hidden;
	}

	.block {
		padding: clamp(3.25rem, 6.5vw, 5.5rem) 1.5rem;
	}

	.block-inner {
		max-width: 1080px;
		margin: 0 auto;
	}

	.block-header {
		text-align: center;
		margin-bottom: 2.75rem;
	}

	.block-eyebrow {
		display: inline-block;
		font-size: 0.85rem;
		font-weight: 700;
		letter-spacing: 0.12em;
		text-transform: uppercase;
		color: var(--blue-deep);
		margin-bottom: 0.7rem;
	}

	.block-heading {
		font-family: "Fraunces", Georgia, "Times New Roman", serif;
		font-size: clamp(1.9rem, 3.2vw, 2.8rem);
		font-weight: 600;
		letter-spacing: -0.02em;
		color: var(--navy);
		margin: 0;
	}

	.block-sub {
		font-size: 1.05rem;
		line-height: 1.6;
		color: var(--ink-soft);
		max-width: 52ch;
		margin: 0.9rem auto 0;
	}

	.btn {
		display: inline-flex;
		align-items: center;
		justify-content: center;
		padding: 0.95rem 1.95rem;
		border-radius: 12px;
		font-family: inherit;
		font-size: 1.05rem;
		font-weight: 600;
		text-decoration: none;
		cursor: pointer;
		border: 1px solid transparent;
		transition: transform 0.2s ease, box-shadow 0.2s ease, background 0.2s ease, border-color 0.2s ease, color 0.2s ease;
	}

	.btn-primary {
		background: var(--blue);
		color: #ffffff;
		box-shadow: 0 14px 28px -14px rgba(37, 99, 235, 0.7);
	}

	.btn-primary:hover {
		transform: translateY(-2px);
		background: var(--blue-deep);
		box-shadow: 0 20px 34px -16px rgba(37, 99, 235, 0.75);
	}

	.btn-primary:focus-visible {
		outline: 3px solid rgba(37, 99, 235, 0.4);
		outline-offset: 2px;
	}

	.btn-ghost {
		background: var(--surface);
		color: var(--ink);
		border-color: var(--border-strong);
	}

	.btn-ghost:hover {
		transform: translateY(-2px);
		border-color: var(--blue);
		color: var(--blue-deep);
	}

	.btn-ghost:focus-visible {
		outline: 3px solid rgba(15, 39, 66, 0.25);
		outline-offset: 2px;
	}

	.btn-on-blue {
		background: #ffffff;
		color: var(--blue-deep);
		box-shadow: 0 14px 30px -14px rgba(0, 0, 0, 0.5);
	}

	.btn-on-blue:hover {
		color: var(--blue-deep);
		transform: translateY(-2px);
		background: #f1f6ff;
		box-shadow: 0 20px 36px -16px rgba(0, 0, 0, 0.55);
	}

	.btn-on-blue:focus-visible {
		outline: 3px solid rgba(255, 255, 255, 0.6);
		outline-offset: 2px;
	}

	.block-hero {
		position: relative;
		isolation: isolate;
		overflow: hidden;
		padding-top: clamp(3.5rem, 8vw, 6.5rem);
		padding-bottom: clamp(3.5rem, 7vw, 5.5rem);
	}

	.hero-bg {
		position: absolute;
		inset: 0;
		z-index: -1;
		pointer-events: none;
	}

	.hero-blob {
		position: absolute;
		border-radius: 50%;
		filter: blur(20px);
	}

	.hero-blob-one {
		top: -180px;
		right: -120px;
		width: 520px;
		height: 520px;
		background: radial-gradient(circle at 30% 30%, rgba(37, 99, 235, 0.16), transparent 70%);
	}

	.hero-blob-two {
		bottom: -200px;
		left: -140px;
		width: 460px;
		height: 460px;
		background: radial-gradient(circle at 60% 40%, rgba(14, 165, 233, 0.14), transparent 70%);
	}

	.hero-grid {
		position: absolute;
		inset: 0;
		background-image:
			linear-gradient(rgba(15, 39, 66, 0.04) 1px, transparent 1px),
			linear-gradient(90deg, rgba(15, 39, 66, 0.04) 1px, transparent 1px);
		background-size: 44px 44px;
		-webkit-mask-image: radial-gradient(ellipse 70% 60% at 50% 30%, #000 30%, transparent 75%);
		mask-image: radial-gradient(ellipse 70% 60% at 50% 30%, #000 30%, transparent 75%);
	}

	.hero-inner {
		max-width: 860px;
		text-align: center;
	}

	.eyebrow {
		display: inline-block;
		padding: 0.45rem 1rem;
		border-radius: 999px;
		background: var(--blue-soft);
		color: var(--blue-deep);
		font-size: 0.82rem;
		font-weight: 700;
		letter-spacing: 0.12em;
		text-transform: uppercase;
		margin-bottom: 1.5rem;
	}

	.hero-title {
		font-family: "Fraunces", Georgia, "Times New Roman", serif;
		font-size: clamp(2.4rem, 5.2vw, 4rem);
		line-height: 1.05;
		font-weight: 600;
		letter-spacing: -0.02em;
		color: var(--navy);
		margin: 0 0 1.5rem;
	}

	.hero-lead {
		font-size: clamp(1.1rem, 1.5vw, 1.3rem);
		line-height: 1.7;
		color: var(--ink-soft);
		max-width: 60ch;
		margin: 0 auto 2.25rem;
	}

	.hero-actions {
		display: flex;
		gap: 1rem;
		justify-content: center;
		flex-wrap: wrap;
		margin-bottom: 2.25rem;
	}

	.hero-highlights {
		display: flex;
		flex-wrap: wrap;
		justify-content: center;
		gap: 0.6rem 1.75rem;
		list-style: none;
		margin: 0;
		padding: 0;
		font-size: 0.96rem;
		font-weight: 500;
		color: var(--ink-soft);
	}

	.hero-highlights li {
		display: inline-flex;
		align-items: center;
		gap: 0.55rem;
	}

	.highlight-dot {
		width: 7px;
		height: 7px;
		border-radius: 50%;
		background: var(--sky);
		flex-shrink: 0;
	}

	.block-stats {
		background: var(--surface);
		border-top: 1px solid var(--border);
		border-bottom: 1px solid var(--border);
	}

	.stats-grid {
		display: grid;
		grid-template-columns: repeat(4, 1fr);
		gap: 1.25rem;
	}

	.stat {
		display: flex;
		flex-direction: column;
		align-items: center;
		text-align: center;
		gap: 0.5rem;
		padding: 1.5rem 1rem;
		border-radius: 16px;
		background: var(--bg);
		border: 1px solid var(--border);
	}

	.stat-value {
		font-family: "Fraunces", Georgia, "Times New Roman", serif;
		font-size: clamp(2.1rem, 3.5vw, 2.9rem);
		font-weight: 600;
		line-height: 1;
		color: var(--blue-deep);
	}

	.stat-label {
		font-size: 0.95rem;
		line-height: 1.4;
		color: var(--ink-soft);
	}

	.block-carousel {
		background: var(--bg);
	}

	.carousel {
		position: relative;
		outline: none;
	}

	.carousel:focus-visible {
		outline: 3px solid rgba(37, 99, 235, 0.35);
		outline-offset: 6px;
		border-radius: 22px;
	}

	.carousel-viewport {
		overflow: hidden;
		border-radius: 22px;
	}

	.carousel-track {
		display: flex;
		transition: transform 0.45s cubic-bezier(0.22, 1, 0.36, 1);
	}

	.carousel-slide {
		flex: 0 0 100%;
		min-width: 0;
		box-sizing: border-box;
	}

	.carousel-card {
		height: 100%;
		box-sizing: border-box;
		padding: clamp(2rem, 4vw, 3.25rem);
		border-radius: 22px;
		background: var(--surface);
		border: 1px solid var(--border);
		box-shadow: 0 30px 60px -44px rgba(15, 39, 66, 0.55);
	}

	.carousel-tag {
		display: inline-block;
		padding: 0.35rem 0.85rem;
		border-radius: 999px;
		background: var(--blue-soft);
		color: var(--blue-deep);
		font-size: 0.78rem;
		font-weight: 700;
		letter-spacing: 0.08em;
		text-transform: uppercase;
		margin-bottom: 1.1rem;
	}

	.carousel-card-title {
		font-family: "Fraunces", Georgia, "Times New Roman", serif;
		font-size: clamp(1.5rem, 2.6vw, 2.1rem);
		font-weight: 600;
		line-height: 1.2;
		color: var(--navy);
		margin: 0 0 0.85rem;
	}

	.carousel-card-text {
		font-size: clamp(1.02rem, 1.3vw, 1.15rem);
		line-height: 1.7;
		color: var(--ink-soft);
		margin: 0;
		max-width: 60ch;
	}

	.carousel-controls {
		display: flex;
		align-items: center;
		justify-content: center;
		gap: 1.25rem;
		margin-top: 1.75rem;
	}

	.carousel-arrow {
		display: inline-flex;
		align-items: center;
		justify-content: center;
		width: 48px;
		height: 48px;
		border-radius: 50%;
		border: 1px solid var(--border-strong);
		background: var(--surface);
		color: var(--blue-deep);
		cursor: pointer;
		flex-shrink: 0;
		transition: transform 0.2s ease, background 0.2s ease, border-color 0.2s ease, box-shadow 0.2s ease;
	}

	.carousel-arrow:hover {
		transform: translateY(-2px);
		border-color: var(--blue);
		box-shadow: 0 16px 28px -18px rgba(37, 99, 235, 0.7);
	}

	.carousel-arrow:focus-visible {
		outline: 3px solid rgba(37, 99, 235, 0.4);
		outline-offset: 2px;
	}

	.carousel-arrow svg {
		width: 22px;
		height: 22px;
	}

	.carousel-dots {
		display: inline-flex;
		align-items: center;
		gap: 0.55rem;
	}

	.carousel-dot {
		width: 10px;
		height: 10px;
		padding: 0;
		border: none;
		border-radius: 999px;
		background: var(--border-strong);
		cursor: pointer;
		transition: width 0.25s ease, background 0.25s ease;
	}

	.carousel-dot:hover {
		background: var(--ink-muted);
	}

	.carousel-dot-active {
		width: 28px;
		background: var(--blue);
	}

	.carousel-dot:focus-visible {
		outline: 3px solid rgba(37, 99, 235, 0.4);
		outline-offset: 3px;
	}

	.block-cards {
		background: var(--surface);
		border-top: 1px solid var(--border);
		border-bottom: 1px solid var(--border);
	}

	.cards-grid {
		display: grid;
		grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
		gap: 1.5rem;
	}

	.content-card {
		padding: 2rem 1.85rem;
		border-radius: 18px;
		background: var(--bg);
		border: 1px solid var(--border);
		transition: transform 0.25s ease, box-shadow 0.25s ease, border-color 0.25s ease;
	}

	.content-card:hover {
		transform: translateY(-4px);
		border-color: var(--blue);
		box-shadow: 0 24px 44px -30px rgba(15, 39, 66, 0.5);
	}

	.content-card-icon {
		display: inline-flex;
		align-items: center;
		justify-content: center;
		width: 52px;
		height: 52px;
		border-radius: 14px;
		background: var(--blue-soft);
		color: var(--blue-deep);
		margin-bottom: 1.25rem;
	}

	.content-card-icon svg {
		width: 26px;
		height: 26px;
	}

	.content-card-title {
		font-size: 1.22rem;
		font-weight: 700;
		color: var(--navy);
		margin: 0 0 0.6rem;
	}

	.content-card-text {
		font-size: 1.02rem;
		line-height: 1.65;
		color: var(--ink-soft);
		margin: 0;
	}

	.block-split {
		background: var(--bg);
	}

	.split-grid {
		display: grid;
		grid-template-columns: 1fr 1fr;
		gap: clamp(2rem, 5vw, 4rem);
		align-items: center;
	}

	.split-flip .split-visual {
		order: 2;
	}

	.split-flip .split-content {
		order: 1;
	}

	.split-visual {
		display: flex;
		align-items: center;
		justify-content: center;
		aspect-ratio: 4 / 3;
		border-radius: 20px;
		background: linear-gradient(135deg, var(--surface-muted), var(--blue-soft));
		border: 1px solid var(--border);
	}

	.split-visual-label {
		font-size: 0.9rem;
		font-weight: 600;
		letter-spacing: 0.08em;
		text-transform: uppercase;
		color: var(--ink-muted);
	}

	.split-heading {
		font-family: "Fraunces", Georgia, "Times New Roman", serif;
		font-size: clamp(1.7rem, 3vw, 2.5rem);
		font-weight: 600;
		letter-spacing: -0.02em;
		line-height: 1.15;
		color: var(--navy);
		margin: 0.4rem 0 1rem;
	}

	.split-text {
		font-size: 1.08rem;
		line-height: 1.7;
		color: var(--ink-soft);
		margin: 0 0 1.5rem;
	}

	.split-bullets {
		list-style: none;
		margin: 0 0 1.75rem;
		padding: 0;
		display: grid;
		gap: 0.85rem;
	}

	.split-bullets li {
		display: flex;
		align-items: flex-start;
		gap: 0.7rem;
		font-size: 1.02rem;
		line-height: 1.55;
		color: var(--ink);
	}

	.split-bullet-icon {
		display: inline-flex;
		align-items: center;
		justify-content: center;
		width: 24px;
		height: 24px;
		border-radius: 50%;
		background: var(--blue-soft);
		color: var(--blue-deep);
		flex-shrink: 0;
		margin-top: 0.1rem;
	}

	.split-bullet-icon svg {
		width: 13px;
		height: 13px;
	}

	.split-cta {
		margin-top: 0.25rem;
	}

	.block-quote {
		background: var(--surface);
		border-top: 1px solid var(--border);
		border-bottom: 1px solid var(--border);
	}

	.quote-inner {
		max-width: 760px;
		text-align: center;
	}

	.quote-mark {
		display: block;
		font-family: "Fraunces", Georgia, serif;
		font-size: 4.5rem;
		line-height: 0.4;
		color: var(--blue);
		margin-bottom: 0.5rem;
	}

	.quote-text {
		font-family: "Fraunces", Georgia, "Times New Roman", serif;
		font-size: clamp(1.4rem, 2.4vw, 1.95rem);
		line-height: 1.45;
		font-weight: 500;
		color: var(--navy);
		margin: 0 0 1.25rem;
	}

	.quote-author {
		margin: 0;
		display: flex;
		flex-direction: column;
		gap: 0.15rem;
	}

	.quote-author-name {
		font-size: 1rem;
		font-weight: 700;
		color: var(--navy);
	}

	.quote-author-role {
		font-size: 0.92rem;
		color: var(--ink-muted);
	}

	.block-cta {
		background: var(--bg);
	}

	.cta-card {
		padding: clamp(3rem, 5vw, 4.5rem) clamp(1.75rem, 4vw, 4rem);
		border-radius: 24px;
		background: linear-gradient(135deg, var(--blue) 0%, var(--sky-deep) 100%);
		color: #ffffff;
		text-align: center;
		box-shadow: 0 34px 64px -36px rgba(37, 99, 235, 0.7);
	}

	.cta-title {
		font-family: "Fraunces", Georgia, "Times New Roman", serif;
		font-size: clamp(1.9rem, 3.5vw, 3rem);
		font-weight: 600;
		line-height: 1.1;
		letter-spacing: -0.02em;
		margin: 0 auto 1.25rem;
		max-width: 22ch;
		color: #ffffff;
	}

	.cta-lead {
		font-size: clamp(1.08rem, 1.4vw, 1.25rem);
		line-height: 1.6;
		color: rgba(255, 255, 255, 0.92);
		max-width: 54ch;
		margin: 0 auto 2.25rem;
	}

	.cta-actions {
		display: flex;
		gap: 1rem;
		justify-content: center;
		flex-wrap: wrap;
		margin-bottom: 1.25rem;
	}

	.cta-note {
		font-size: 0.98rem;
		color: rgba(255, 255, 255, 0.85);
		margin: 0;
	}

	.rise {
		opacity: 0;
		transform: translateY(16px);
		animation: rise-in 0.6s cubic-bezier(0.22, 1, 0.36, 1) forwards;
	}

	.rise-1 { animation-delay: 0.08s; }
	.rise-2 { animation-delay: 0.16s; }
	.rise-3 { animation-delay: 0.24s; }
	.rise-4 { animation-delay: 0.32s; }

	@keyframes rise-in {
		to {
			opacity: 1;
			transform: none;
		}
	}

	@media (max-width: 900px) {
		.stats-grid {
			grid-template-columns: repeat(2, 1fr);
		}

		.split-grid {
			grid-template-columns: 1fr;
		}

		.split-flip .split-visual,
		.split-flip .split-content {
			order: 0;
		}
	}

	@media (max-width: 640px) {
		.hero-actions,
		.cta-actions {
			flex-direction: column;
			align-items: stretch;
		}

		.btn {
			width: 100%;
		}

		.carousel-card-text {
			font-size: 1.02rem;
		}
	}

	@media (prefers-reduced-motion: reduce) {
		.rise {
			opacity: 1;
			transform: none;
			animation: none;
		}

		.carousel-track {
			transition: none;
		}
	}
</style>